import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import * as Sentry from '@sentry/angular';
import { ANALYTICS_FALLBACK_USER_NAME } from '@shared';
import { from, of, timer } from 'rxjs';
import {
  catchError,
  exhaustMap,
  map,
  mergeMap,
  switchMap,
  tap,
} from 'rxjs/operators';

import * as fromGenerated from '../../_generated';
import * as fromChats from '../../chats';
import * as fromFavorites from '../../favorites';
import * as fromFeatureFlags from '../../feature-flags';
import * as fromRefiner from '../../refiner';

import { HttpContext } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { environment } from '../../../environments/environment';
import { resolveSupportedLanguage } from '../../app.i18n';
import { IS_AUTH_INTERCEPTOR_ENABLED } from '../auth.interceptor';
import { AuthActions } from './auth.actions';
import { AuthState } from './auth.reducer';

@Injectable()
export class AuthEffects {
  private readonly actions$ = inject(Actions);
  private readonly userService = inject(fromGenerated.UsersService);
  private readonly authService = inject(fromGenerated.AuthService);
  private readonly store = inject(Store<AuthState>);
  private readonly translateService = inject(TranslateService);
  private readonly refinerService = inject(fromRefiner.RefinerService);

  login$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.login),
        switchMap(
          ({ idpCustomId }) =>
            (window.location.href = `${environment.appApiUrl}/v1/auth/login?identityProvider=${idpCustomId}`),
        ),
      ),
    { dispatch: false },
  );

  logout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.logout),
        tap(() => Sentry.setUser(null)),
        mergeMap(() => from(this.refinerService.reset())),
        switchMap(
          () =>
            (window.location.href = `${environment.appApiUrl}/v1/auth/logout`),
        ),
      ),
    { dispatch: false },
  );

  authenticateUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.authenticateUser),
      switchMap(() =>
        this.userService
          .usersControllerGetMeV1(undefined, undefined, {
            context: new HttpContext().set(IS_AUTH_INTERCEPTOR_ENABLED, false),
          })
          .pipe(
            map((user) => AuthActions.authenticateUserSuccess({ user: user })),
            catchError((error: Error) =>
              of(AuthActions.authenticateUserFailure({ error: error.message })),
            ),
          ),
      ),
    ),
  );

  authenticateUserSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.authenticateUserSuccess),
      tap(({ user }) => {
        Sentry.setUser({
          id: user.id,
          email: user.username,
          username: user.fullName ?? ANALYTICS_FALLBACK_USER_NAME,
        });
      }),
      mergeMap(async ({ user }) => {
        const locale = resolveSupportedLanguage(
          this.translateService.getCurrentLang(),
          environment.defaultLanguage,
        );
        await this.refinerService.identify(
          {
            id: user.id,
            email: user.email,
            name: user.fullName,
            locale,
          },
          user.refinerSignature,
        );
        return { user };
      }),
      switchMap(({ user }) => [
        AuthActions.startRefreshTokensTimer(),
        fromFeatureFlags.FeatureFlagsActions.identifyUser({ user }),
        fromFavorites.AssistantFavoritesActions.load(),
      ]),
    ),
  );

  startRefreshTokensInterval$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.startRefreshTokensTimer),
        switchMap(() =>
          timer(environment.refreshTokensTimerDue).pipe(
            tap(() => this.store.dispatch(AuthActions.refreshTokens())),
          ),
        ),
      ),
    { dispatch: false },
  );

  refreshTokens$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.refreshTokens),
      exhaustMap(() =>
        this.authService.authControllerRefreshTokensV1().pipe(
          map(() => {
            return AuthActions.refreshTokensSuccess();
          }),
          catchError((error: Error) =>
            of(AuthActions.refreshTokensFailure({ error: error.message })),
          ),
        ),
      ),
    ),
  );

  refreshTokensSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.refreshTokensSuccess),
      switchMap(() => [
        AuthActions.startRefreshTokensTimer(),
        fromChats.ChatsSocketActions.reconnect(),
      ]),
    ),
  );
}
